import React from 'react';
import { 
  ArrowUpRight,
  MoreHorizontal,
  ShoppingCart,
  Users,
  CreditCard,
  Calendar
} from 'lucide-react';

const StatCard = ({ title, amount, trend, icon: Icon, bgColor = 'bg-peach-50' }) => (
  <div className={`${bgColor} rounded-2xl p-6`}>
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-gray-700" />
        <h3 className="text-gray-800 text-lg font-medium">{title}</h3>
      </div>
      <ArrowUpRight className="w-5 h-5 text-gray-600" />
    </div>
    <p className="text-3xl font-semibold mb-2">₹{amount}</p>
    <p className="text-gray-600 text-sm">{trend} vs last month</p>
  </div>
);

const DateRangeSelector = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <Calendar className="w-4 h-4 text-gray-500" />
    <select 
      value={value} 
      onChange={(e) => onChange(e.target.value)}
      className="text-sm border-0 bg-transparent focus:ring-0 text-gray-700 font-medium cursor-pointer"
    >
      <option value="7">Last 7 days</option>
      <option value="30">Last 30 days</option>
      <option value="1">Today</option>
      <option value="yesterday">Yesterday</option>
      <option value="90">Last 90 days</option>
      <option value="year">This year</option>
      <option value="custom">Custom dates</option>
    </select>
  </div>
);

const SaleRow = ({ customer, product, date, status, amount }) => (
  <div className="flex items-center justify-between py-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-gray-100">
        <img src={`https://i.pravatar.cc/40?u=${customer}`} alt="" className="rounded-full" />
      </div>
      <div>
        <p className="font-medium text-gray-900">{customer}</p>
        <p className="text-sm text-gray-500">{product}</p>
      </div>
    </div>
    <div className="flex items-center gap-4">
      <span className={`px-3 py-1 rounded-full text-sm ${
        status === 'Pending' 
          ? 'bg-yellow-50 text-yellow-700' 
          : status === 'Completed'
          ? 'bg-green-50 text-green-700'
          : 'bg-red-50 text-red-700'
      }`}>
        {status}
      </span>
      <span className="font-medium text-gray-900">₹{amount}</span>
      <button className="text-gray-400 hover:text-gray-600">
        <MoreHorizontal className="w-5 h-5" />
      </button>
    </div>
  </div>
);

export const Dashboard = () => {
  const [dateRange, setDateRange] = React.useState('30');

  return (
    <div className="p-8">
      {/* Top Stats Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Overview</h2>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        <StatCard 
          title="Total Sales" 
          amount="97,450" 
          trend="+8.8%"
          icon={ShoppingCart}
          bgColor="bg-peach-50"
        />
        <StatCard 
          title="Customers" 
          amount="1,234" 
          trend="+12.3%"
          icon={Users}
          bgColor="bg-mint-50"
        />
        <StatCard 
          title="Avg. Order Value" 
          amount="2,160" 
          trend="+7.9%"
          icon={CreditCard}
          bgColor="bg-sky-50"
        />
      </div>

      {/* Revenue Chart */}
      <div className="bg-white rounded-3xl p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Revenue Overview</h2>
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
        </div>

        <div className="h-64 flex items-end justify-between">
          {/* Chart bars will be dynamically generated based on dateRange */}
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex flex-col items-center gap-2">
              <div 
                className="w-12 bg-indigo-100 rounded-t-lg hover:bg-indigo-200 transition-colors"
                style={{ height: `${Math.random() * 200}px` }}
              />
              <span className="text-sm text-gray-600">Label</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Sales */}
      <div className="bg-white rounded-3xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Recent Sales</h2>
          <div className="flex gap-2">
            <button className="px-4 py-1 bg-gray-100 rounded-full text-sm font-medium">Today</button>
            <button className="px-4 py-1 text-gray-500 text-sm font-medium">Yesterday</button>
          </div>
        </div>

        <div className="divide-y">
          <SaleRow 
            customer="Sarah Johnson"
            product="Premium Course"
            date="2 min ago"
            status="Completed"
            amount="2,999"
          />
          <SaleRow 
            customer="Michael Chen"
            product="Digital Template"
            date="15 min ago"
            status="Pending"
            amount="1,499"
          />
          <SaleRow 
            customer="Emily Davis"
            product="Consulting Call"
            date="1 hour ago"
            status="Completed"
            amount="5,000"
          />
          <SaleRow 
            customer="Alex Thompson"
            product="E-book Bundle"
            date="2 hours ago"
            status="Refunded"
            amount="799"
          />
          <SaleRow 
            customer="Lisa Anderson"
            product="Annual Subscription"
            date="3 hours ago"
            status="Completed"
            amount="12,000"
          />
        </div>
      </div>
    </div>
  );
};